import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { filterAndSort } from 'src/app/search.utils';
import { LocationGroupService } from 'src/app/service/location-group.service';
import { PanModalComponent } from '../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../error.utils';
import { NotifyService } from '../../service/notify.service';
import { UserLoginService } from '../../service/user-login.service';
import { LocationGroup } from '../../types/location-group-response';

@Component({
  selector: 'app-admin-user-management-location-groups-whitelist',
  templateUrl: './admin-user-management-location-groups-whitelist.component.html',
  styleUrl: './admin-user-management-location-groups-whitelist.component.scss',
  standalone: false,
})
export class AdminUserManagementLocationGroupsWhitelistComponent implements OnInit {
  private locationGroupService = inject(LocationGroupService);
  private notifications = inject(NotifyService);
  public userLogin = inject(UserLoginService);

  public selectedLocationGroup = computed(() => {
    const id = this.selectedLocationGroupId();
    if (id == null) return undefined;
    return this.locationGroups().find((g) => g.id === id);
  });
  public selectedLocationGroupId = signal<string | undefined>(undefined);
  public hasLoadedUserLocationGroups = signal<boolean>(false);
  public locationGroups = signal<LocationGroup[]>([]);
  public locationGroupFilter = signal<string>('');
  public selectedLocationGroupDomains = computed(() => this.selectedLocationGroup()?.domain_whitelist || []);
  public domainFilter = signal<string>('');
  public filteredDomains = computed(() => {
    const filter = this.domainFilter().toLowerCase();
    if (!filter) return this.selectedLocationGroupDomains();
    return this.selectedLocationGroupDomains().filter((d) => d.toLowerCase().includes(filter));
  });
  public addDomainModal = viewChild<PanModalComponent>('addDomainModal');

  public locationGroupId = computed(() => this.selectedLocationGroup()?.id.toString());

  public visibleLocationGroups = computed(() => filterAndSort(this.locationGroups(), this.locationGroupFilter()));
  public newDomain = signal<string>('');
  public canSubmitModal = computed(() => !!this.selectedLocationGroup() && this.newDomain().trim().length > 0);

  constructor() {}

  ngOnInit() {
    this.loadLocationGroups();
  }

  loadLocationGroups(): void {
    this.hasLoadedUserLocationGroups.set(false);
    this.locationGroups.set([]);

    this.locationGroupService.getAllLocationGroups().subscribe((resp) => {
      this.locationGroups.set(resp.items);
      this.hasLoadedUserLocationGroups.set(true);
    });
  }

  selectLocationGroup(locationGroup: LocationGroup): void {
    this.domainFilter.set('');
    this.selectedLocationGroupId.set(locationGroup.id);
  }

  removeDomain(domain: string): void {
    if (!this.selectedLocationGroup()) return;
    this.locationGroupService
      .updateLocationGroupWhitelist(this.selectedLocationGroup().id, {
        domain_whitelist: (this.selectedLocationGroup().domain_whitelist || []).filter((d) => d !== domain),
      })
      .subscribe((resp) => {
        this.updateGroup(resp);
        this.notifications.notify(`Successfully removed domain "${domain}" from location group "${this.selectedLocationGroup().name}"`);
      });
  }

  submitModal(): void {
    const locationGroup = this.selectedLocationGroup();
    if (!locationGroup) return;

    const domain = this.newDomain().trim();
    if (!domain) return;

    const existingDomains = locationGroup.domain_whitelist || [];

    if (existingDomains.includes(domain)) {
      this.notifications.notify(`Domain "${domain}" already exists in the whitelist`);
      return;
    }

    this.locationGroupService
      .updateLocationGroupWhitelist(locationGroup.id, {
        domain_whitelist: [...existingDomains, domain],
      })
      .subscribe({
        next: (resp) => {
          this.updateGroup(resp);
          this.notifications.notify(`Successfully added domain "${domain}" to location group "${locationGroup.name}"`);
          this.addDomainModal().hide();
        },
        error: (resp) => {
          this.notifications.notify(tryGetErrorMessage(resp));
        },
        complete: () => this.newDomain.set(''),
      });
  }

  updateGroup(locationGroup: LocationGroup): void {
    this.locationGroups.update((groups) => groups.map((g) => (g.id === locationGroup.id ? locationGroup : g)));
  }
}
